import { useState } from 'react';
import { LifeEvent } from '../hooks/useLifeEvents';
import { PanelBase } from './PanelBase';
import { TableViewBase } from './TableViewBase';
import { showSuccess, showError } from 'src/toasts/toasts';
import { amountCell } from '../components/cashflowCells';
import { dateOrNotRecorded, truncatedTextOrNotRecorded } from '../notRecorded';
import { LifeEventModal } from './LifeEventModal';
import { useDisclosure } from '@mantine/hooks';
import { useCurrency } from 'src/common/hooks/useCurrency';

type LifeEventPanelProps = {
  items: LifeEvent[];
  onSave: (data: LifeEvent[]) => Promise<void>;
  onDelete: (ids: string[]) => Promise<void>;
};

export function LifeEventPanel({ items = [], onSave, onDelete }: LifeEventPanelProps) {
  const currency = useCurrency();
  const [modalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);
  const [selectedLifeEvent, setSelectedLifeEvent] = useState<LifeEvent | undefined>();

  // Reports the failure and lets it through: the modal keeps what was typed only if it hears
  // that the save failed, and the adviser would otherwise have to retype the whole row.
  const handleSave = async (lifeEvent: Omit<LifeEvent, 'id' | 'clientId'>) => {
    try {
      await onSave([{ ...lifeEvent, id: selectedLifeEvent?.id } as LifeEvent]);
    } catch (error) {
      showError('Failed to save life event');
      throw error;
    }
    showSuccess('Life event saved successfully');
    closeModal();
    setSelectedLifeEvent(undefined);
  };

  const handleEdit = (lifeEvent: LifeEvent) => {
    setSelectedLifeEvent(lifeEvent);
    openModal();
  };

  const handleAdd = () => {
    setSelectedLifeEvent(undefined);
    openModal();
  };

  const handleDelete = async (lifeEvent: LifeEvent) => {
    if (!lifeEvent.id) return;
    try {
      await onDelete([lifeEvent.id]);
      showSuccess('Life event deleted');
    } catch {
      showError('Failed to delete life event');
    }
  };

  const renderTableView = () => (
    <TableViewBase
      items={items}
      columns={[
        {
          key: 'description',
          header: 'Description',
          sortable: true,
          renderValue: (_value, item) => truncatedTextOrNotRecorded(item.description),
        },
        {
          key: 'eventDate',
          header: 'Date',
          sortable: true,
          renderValue: (_value, item) => dateOrNotRecorded(item.eventDate),
        },
        { key: 'details', header: 'Details', sortable: true },
        {
          key: 'cost',
          header: 'Amount',
          sortable: true,
          renderValue: (_value, item) => amountCell(item.cost, currency),
          align: 'right',
        },
      ]}
      onEdit={handleEdit}
      onDelete={handleDelete}
    />
  );

  return (
    <>
      <PanelBase<LifeEvent>
        items={items}
        addButtonLabel="Add a new life event"
        renderTableView={renderTableView}
        onAdd={handleAdd}
      />
      <LifeEventModal
        opened={modalOpened}
        onClose={closeModal}
        onSubmit={handleSave}
        initialValues={selectedLifeEvent}
      />
    </>
  );
}
